import React from 'react';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { HomeIcon, HealthGraphIcon, MessageIcon, SettingsIcon } from '../Icons';
import { usePatient } from '../../hooks';

const Nav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  justify-content: space-around;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  z-index: 100;

  body[data-keyboard-open='true'] & {
    display: none;
  }
`;

const NavItem = styled(NavLink)`
  flex: 1;
  background: none;
  border: none;
  padding: 5px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.icon};
  transition: color 0.2s;

  &.active {
    color: ${({ theme }) => theme.colors.primary};
    cursor: default;
  }
`;

const NavIcon = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: inherit;
`;

interface NavigationItem {
  path: string;
  icon: React.FC<{ color?: string }>;
  label: string;
  hash?: string;
  preserveDateParam?: boolean; // 日付パラメータを引き継ぐか
  selfOnly?: boolean; // 本人（relationship === '本人'）のみ表示
}

const navigationItems: NavigationItem[] = [
  { path: '/', icon: HomeIcon, label: 'ホーム' },
  { path: '/health-graph', icon: HealthGraphIcon, label: '健康グラフ', preserveDateParam: true },
  { path: '/message', hash: '#history', icon: MessageIcon, label: '連絡' },
  // 一時的に動線を閉鎖中 (PR #344)。復活時は ../Icons から AiChatIcon を再 import する
  // { path: '/ai-chat', icon: AiChatIcon, label: 'おはなし', selfOnly: true },
  { path: '/settings', icon: SettingsIcon, label: '設定' },
];

export const BottomNavigation: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { linkedPatients } = usePatient();

  const hasSelf = linkedPatients?.some(p => p.relationship === '本人') ?? false;

  const visibleItems = navigationItems.filter(item => !item.selfOnly || hasSelf);

  const isActive = (itemPath: string): boolean => {
    if (itemPath === '/') return location.pathname === '/';
    return location.pathname.startsWith(itemPath);
  };

  // 各ナビゲーションアイテムの遷移先を生成
  const getNavigationPath = (item: NavigationItem): string => {
    const params = new URLSearchParams();

    // 日付パラメータを引き継ぐ
    if (item.preserveDateParam) {
      const dateParam = searchParams.get('date');
      if (dateParam) {
        params.append('date', dateParam);
      }
    }

    // 患者IDパラメータを引き継ぐ（全てのページで）
    const patientIdParam = searchParams.get('patient_id');
    if (patientIdParam) {
      params.append('patient_id', patientIdParam);
    }

    const queryString = params.toString();
    const hash = item.hash || '';
    return queryString ? `${item.path}?${queryString}${hash}` : `${item.path}${hash}`;
  };

  return (
    <Nav>
      {visibleItems.map((item) => {
        const active = isActive(item.path);
        const IconComponent = item.icon;
        return (
          <NavItem
            key={item.path}
            to={getNavigationPath(item)}
            end={item.path === '/'}
          >
            <NavIcon>
              <IconComponent color={active ? '#00A960' : '#888888'} />
            </NavIcon>
            <NavLabel>{item.label}</NavLabel>
          </NavItem>
        );
      })}
    </Nav>
  );
};

export default BottomNavigation;
